// Copyright (c) Ping. All rights reserved.
//
// In-call thread parity wiring. Builds the SAME `rowActions` + rich
// `composerProps` the main chat thread uses (reactions, message actions,
// retry, read-receipt resolvers, media variants, mentions/attachments/voice/
// GIF composer) so the in-call "Call Thread" panel is feature-equivalent to
// the chat-room thread it shares a root with — no longer text-only.
//
// This deliberately reuses the SHARED, already-enriched `createWebThreadGateway`
// (read receipts + failed-status projection) and the same send helpers the
// main chat uses, rather than re-deriving the ChatRouteHost media-decryption
// pipeline. Interactive image/video/live-poll tiles fall back to the
// context-free display variant here (they need the host's S3-decrypt state);
// voice playback, docs, location, and link previews are fully interactive.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { liveQuery } from 'dexie';

import {
  type ChatMessageVariant,
  deriveVariantFromBody,
  encodeEnvelopeJsonBytes,
  ForwardOverlay,
  gameEventsFromMessages,
  GamePickerOverlay,
  ImageLightbox,
  lastMessagePreview,
  type MentionCandidate,
  type Message,
  type MessageMenuAction,
  parseEnvelope,
  persistSentMessage as sharedPersistSentMessage,
  pingToast,
  type PollViewContent,
  type PollVoterAvatar,
  PollVotesPanel,
  type StagedAttachment,
  type ThreadComposerProps,
  type ThreadGateway,
  type ThreadIdentifier,
  type ThreadReply,
  type ThreadRowActions,
  useChatRoomShellViewModel,
  useGameSessions,
  useReactions,
  type VoiceSource,
} from '@ping/ui';

import { log } from '@core/log/log';
import { db, webPollVotesPersister, webReactionsPersister } from '@core/persistence/db';
import { retrieveIdentity } from '@core/persistence/identity';
import { PollComposerHost, ShareLocationHost } from '@features/chat';
import { fetchAndDecryptMediaBlob } from '@features/chat/fetchMediaBlob';
import { addLocallyRemovedMessage } from '@features/chat/locallyRemovedMessages';
import { resolveCurrentLocation, searchPlaces } from '@features/chat/locationProviders';
import {
  webConversationsPersister,
  webMessagesPersister,
} from '@features/chat/persistenceAdapters';
import {
  webPinnedPersister,
  webSavedPersister,
  webStarredPersister,
} from '@features/chat/personalBookmarks';
import { resolveActiveConv } from '@features/chat/resolveActiveConv';
import { parseFileEnvelope } from '@features/chat/sendFileMessage';
import { parseImageEnvelope } from '@features/chat/sendImageMessage';
import { parseVideoEnvelope } from '@features/chat/sendVideoMessage';
import { parseVoiceEnvelope } from '@features/chat/sendVoiceMessage';
import { useThreadComposer } from '@features/chat/useThreadComposer';
import { ProfileOverlaysHost } from '@features/profile/ProfileOverlaysHost';
import { sendStar } from '@features/tasks/deviceGroupTaskSync';

const CALL_THREAD_WAVEFORM: readonly number[] = [
  0.3, 0.5, 0.7, 0.9, 0.6, 0.4, 0.5, 0.8, 0.7, 0.5, 0.3, 0.6, 0.9, 0.7, 0.5, 0.4, 0.6, 0.8, 0.5,
  0.3, 0.4, 0.6, 0.5, 0.3,
];

function threadReplyMessage(reply: ThreadReply): Message {
  return {
    id: reply.id as unknown as string,
    conversationId: reply.threadId as unknown as string,
    senderId: reply.authorId as unknown as string,
    body: reply.body,
    hlc: '',
    contentHash: '',
    timestamp: reply.timestamp,
    isSelf: reply.isSelf,
    deliveryState: 'sent',
  };
}

function resolveCallVoiceSource(message: Message): VoiceSource | null {
  const payload = parseVoiceEnvelope(message.body);
  if (payload === undefined) return null;
  return { kind: 'macos-base64', base64: payload.bytes, mime: payload.mime };
}

function envelopeKind(body: string | undefined): string | undefined {
  if (body === undefined || body.length === 0 || body.charAt(0) !== '{') return undefined;
  try {
    const parsed = JSON.parse(body) as { kind?: unknown };
    return typeof parsed.kind === 'string' ? parsed.kind : undefined;
  } catch {
    return undefined;
  }
}

export interface CallThreadWiringParams {
  /** Conversation the call (and its thread) lives in. */
  readonly conversationId: string | null;
  /** SDK client (session). */
  readonly client: unknown | null;
  /** Conversation display name (for MLS resolve). */
  readonly conversationName?: string | undefined;
  /** The thread gateway (drives the media-resolution subscription). */
  readonly gateway: ThreadGateway;
  /** Open call-thread root content hash (null when none) — the media pipeline. */
  readonly openThreadId: string | null;
  /** Live ref to the open call-thread root content hash (null when none). */
  readonly threadRootRef: React.RefObject<string | null>;
  /** Group roster maps for author + reader resolution. */
  readonly roster: {
    readonly memberDisplayNames?: Record<string, string> | undefined;
    readonly memberDeviceNames?: Record<string, string> | undefined;
    readonly memberDeviceAccounts?: Record<string, string> | undefined;
    readonly memberAvatarUris?: Record<string, string> | undefined;
    readonly memberAccountIds?: readonly string[] | undefined;
    readonly isGroup: boolean;
  };
  /** Forward-picker targets (other conversations). */
  readonly forwardTargets: ReadonlyArray<{ readonly id: string; readonly name: string }>;
}

export interface CallThreadWiring {
  readonly rowActions: ThreadRowActions;
  readonly composerProps: ThreadComposerProps;
  readonly stagedAttachments: readonly StagedAttachment[];
  readonly onRemoveStagedAttachment: (id: string) => void;
  readonly onFlushStagedAttachments: () => Promise<void>;
  /** Route-level forward overlay for a thread reply's Forward action. */
  readonly forwardOverlay: React.ReactElement;
  /**
   * Call-surface overlay host — mounts the profile popover/modal + the
   * poll-composer / share-location / game-picker overlays that back the
   * in-call thread's avatar tap, @mention tap, and `+` → poll/location/game
   * creation. The call surface has no `ChatRoomShellViewModel` of its own, so
   * this bundles a call-local one. Mount it once at the call view root.
   */
  readonly overlayHost: React.ReactElement;
}

/** Lowercase hex of a content-hash byte array (local-echo `contentHash`). */
function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i += 1) out += (b[i] ?? 0).toString(16).padStart(2, '0');
  return out;
}

/** Fresh local-echo row id. */
function localEchoId(prefix: string): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Builds the full in-call thread wiring. Safe to call unconditionally; it
 * no-ops gracefully when `conversationId` is null.
 */
export function useCallThreadWiring(params: CallThreadWiringParams): CallThreadWiring {
  const {
    conversationId,
    client,
    conversationName,
    gateway,
    openThreadId,
    threadRootRef,
    roster,
    forwardTargets,
  } = params;

  const [localAccountId, setLocalAccountId] = useState<string | null>(null);
  const localAccountIdRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void retrieveIdentity().then((id) => {
      if (cancelled || id === null) return;
      localAccountIdRef.current = id.accountId;
      setLocalAccountId(id.accountId);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Media-resolution pipeline (parity with the main chat thread). Mirror of
  // ChatRouteHost's thread media effects: decrypt S3 image/video blobs + project
  // live poll tallies for the OPEN thread's replies, keyed by reply id, so the
  // in-call thread's image/video/poll tiles are fully interactive (lightbox,
  // inline playback, live vote counts) rather than display-only.
  const [threadReplies, setThreadReplies] = useState<
    ReadonlyArray<{ id: string; rawBody: string | undefined }>
  >([]);
  useEffect(() => {
    if (openThreadId === null) {
      setThreadReplies([]);
      return;
    }
    const project = (snap: { replies: readonly ThreadReply[] }): void => {
      setThreadReplies(
        snap.replies.map((r) => ({ id: r.id as unknown as string, rawBody: r.rawBody })),
      );
    };
    const unsub = gateway.subscribe(openThreadId as ThreadIdentifier, project);
    void gateway
      .load(openThreadId as ThreadIdentifier)
      .then(project)
      .catch(() => undefined);
    return () => unsub();
  }, [openThreadId, gateway]);

  // reply id → poll content hash (the poll-vote `target`).
  const [pollTargetByReplyId, setPollTargetByReplyId] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const reply of threadReplies) {
        if (reply.rawBody === undefined) continue;
        if (envelopeKind(reply.rawBody) !== 'com.ping.poll') continue;
        const row = await db.messages.get(reply.id);
        const hash = row?.contentHash;
        if (hash !== undefined && hash.length > 0) next[reply.id] = hash;
      }
      if (!cancelled) setPollTargetByReplyId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [threadReplies]);

  // Personal-bookmark state for the parent conversation, so the thread action
  // menu reflects pinned / saved / starred state (Unpin / Unsave / Unstar).
  const [hashByReplyId, setHashByReplyId] = useState<Record<string, string>>({});
  const [pinnedHashes, setPinnedHashes] = useState<ReadonlySet<string>>(new Set());
  const [savedHashes, setSavedHashes] = useState<ReadonlySet<string>>(new Set());
  const [starredHashes, setStarredHashes] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next: Record<string, string> = {};
      for (const reply of threadReplies) {
        const row = await db.messages.get(reply.id);
        const hash = row?.contentHash;
        if (hash !== undefined && hash.length > 0) next[reply.id] = hash;
      }
      if (!cancelled) setHashByReplyId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [threadReplies]);
  useEffect(() => {
    if (conversationId === null) {
      setPinnedHashes(new Set());
      setSavedHashes(new Set());
      return undefined;
    }
    const unsubPin = webPinnedPersister.subscribe?.(conversationId, (t) => setPinnedHashes(new Set(t)));
    const unsubSave = webSavedPersister.subscribe?.(conversationId, (t) => setSavedHashes(new Set(t)));
    return () => {
      unsubPin?.();
      unsubSave?.();
    };
  }, [conversationId]);
  useEffect(() => webStarredPersister.subscribe?.((t) => setStarredHashes(new Set(t))), []);

  // Decrypted image preview object URLs, keyed by reply id.
  const [imagePreviewByReplyId, setImagePreviewByReplyId] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    const minted: string[] = [];
    for (const reply of threadReplies) {
      if (reply.rawBody === undefined) continue;
      const payload = parseImageEnvelope(reply.rawBody);
      if (payload === undefined || payload.blob_ref.media_id === 'pending') continue;
      void fetchAndDecryptMediaBlob(payload.blob_ref)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          minted.push(url);
          setImagePreviewByReplyId((prev) => {
            if (prev[reply.id] !== undefined) {
              URL.revokeObjectURL(url);
              return prev;
            }
            return { ...prev, [reply.id]: url };
          });
        })
        .catch((err) => {
          log.warn({ event: 'call.thread.image.fetch.failed', err: (err as Error).message });
        });
    }
    return () => {
      cancelled = true;
      for (const url of minted) URL.revokeObjectURL(url);
    };
  }, [threadReplies]);

  // Decrypted poster + video object URLs, keyed by reply id.
  const [videoByReplyId, setVideoByReplyId] = useState<
    Record<string, { videoUri?: string; posterUri?: string }>
  >({});
  useEffect(() => {
    let cancelled = false;
    const minted: string[] = [];
    for (const reply of threadReplies) {
      if (reply.rawBody === undefined) continue;
      const payload = parseVideoEnvelope(reply.rawBody);
      if (payload === undefined || payload.blob_ref.media_id === 'pending') continue;
      const setUri = (key: 'videoUri' | 'posterUri', url: string): void => {
        setVideoByReplyId((prev) => {
          const existing = prev[reply.id];
          if (existing?.[key] !== undefined) {
            URL.revokeObjectURL(url);
            return prev;
          }
          return { ...prev, [reply.id]: { ...existing, [key]: url } };
        });
      };
      if (payload.poster_ref !== undefined) {
        void fetchAndDecryptMediaBlob(payload.poster_ref)
          .then((blob) => {
            if (cancelled) return;
            const url = URL.createObjectURL(blob);
            minted.push(url);
            setUri('posterUri', url);
          })
          .catch(() => undefined);
      }
      void fetchAndDecryptMediaBlob(payload.blob_ref)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          minted.push(url);
          setUri('videoUri', url);
        })
        .catch((err) => {
          log.warn({ event: 'call.thread.video.fetch.failed', err: (err as Error).message });
        });
    }
    return () => {
      cancelled = true;
      for (const url of minted) URL.revokeObjectURL(url);
    };
  }, [threadReplies]);

  // Live poll-vote projection keyed by poll target hash.
  const [pollVotesByTarget, setPollVotesByTarget] = useState<
    Record<
      string,
      {
        counts: Record<number, number>;
        myVote?: number;
        myVotes?: number[];
        total: number;
        votersByOption: Record<number, string[]>;
      }
    >
  >({});
  useEffect(() => {
    const targets = Object.values(pollTargetByReplyId).filter((h): h is string => !!h && h.length > 0);
    if (targets.length === 0) return;
    const me = localAccountId ?? undefined;
    const apply = (
      target: string,
      rows: ReadonlyArray<{ actorAccountId: string; optionIndex: number }>,
    ): void => {
      const counts: Record<number, number> = {};
      const votersByOption: Record<number, string[]> = {};
      const myVotes: number[] = [];
      const actors = new Set<string>();
      for (const r of rows) {
        counts[r.optionIndex] = (counts[r.optionIndex] ?? 0) + 1;
        (votersByOption[r.optionIndex] ??= []).push(r.actorAccountId);
        actors.add(r.actorAccountId);
        if (me !== undefined && r.actorAccountId === me) myVotes.push(r.optionIndex);
      }
      const myVote = myVotes.length > 0 ? myVotes[0] : undefined;
      setPollVotesByTarget((prev) => ({
        ...prev,
        [target]: {
          counts,
          total: actors.size,
          votersByOption,
          ...(myVote !== undefined ? { myVote } : {}),
          ...(myVotes.length > 0 ? { myVotes } : {}),
        },
      }));
    };
    const unsubs = targets.map((target) => {
      void webPollVotesPersister.listByPoll(target).then((rows) => apply(target, rows));
      return (
        webPollVotesPersister.subscribe?.(target, (rows) => apply(target, rows)) ?? (() => undefined)
      );
    });
    return () => unsubs.forEach((u) => u());
  }, [pollTargetByReplyId, localAccountId]);

  const pollVotesByTargetRef = useRef(pollVotesByTarget);
  pollVotesByTargetRef.current = pollVotesByTarget;
  const pollTargetByReplyIdRef = useRef(pollTargetByReplyId);
  pollTargetByReplyIdRef.current = pollTargetByReplyId;
  const threadRepliesRef = useRef(threadReplies);
  threadRepliesRef.current = threadReplies;

  // Fullscreen image lightbox + "View votes" panel state.
  const [lightboxUri, setLightboxUri] = useState<string | null>(null);
  const [votesPanel, setVotesPanel] = useState<{
    poll: PollViewContent;
    counts: Record<number, number>;
    total: number;
    votersByOption?: Record<number, ReadonlyArray<PollVoterAvatar>>;
  } | null>(null);

  const reactionsByMessageId = useReactions(conversationId ?? '', webReactionsPersister);

  // @mention candidates from the group roster (same as the main chat).
  const mentionCandidates = useMemo<ReadonlyArray<MentionCandidate>>(() => {
    const names = roster.memberDisplayNames;
    if (names === undefined) return [];
    return Object.entries(names).map(([userId, displayName]) => ({ userId, displayName }));
  }, [roster.memberDisplayNames]);

  // Best-effort opponent label for a game tile — the first non-self member.
  const gameOpponentName = useMemo<string | undefined>(() => {
    const names = roster.memberDisplayNames;
    if (names === undefined) return undefined;
    const entry = Object.entries(names).find(([id]) => id !== localAccountId);
    return entry?.[1];
  }, [roster.memberDisplayNames, localAccountId]);

  // ── Call-local shell VM (profile popover/modal + poll/location overlays) ──
  // The call surface has no `ChatRoomShellViewModel`; stand up a local one so
  // the in-call thread gets the SAME profile popover + poll / share-location
  // composers the main chat thread uses. The game picker stays on a plain
  // boolean (parity with ChatRouteHost). Mounted via `overlayHost` below.
  const vm = useChatRoomShellViewModel();
  const [gamePickerOpen, setGamePickerOpen] = useState(false);
  // Stamped with the open thread root right before an overlay opens; read +
  // cleared on submit so the created poll/location/game nests under the thread.
  const overlayThreadTargetRef = useRef<string | null>(null);

  // In-call thread game sessions — the live, folded session per create/move so
  // a game reply tile is interactive (parity with the main chat thread).
  const { createGame, applyGameEvent, sessions: gameSessions, makeMove, replaceFromEvents } =
    useGameSessions(conversationId ?? '');

  // Fold the CONVERSATION's persisted game messages into the projection so a
  // thread game tile renders the live phase/moves (and survives a panel
  // remount / shows peer-created games). A game's create + moves span the whole
  // conversation, so we subscribe to the full message list — NOT just the
  // thread replies. Mirrors ChatRouteHost's `persistedThreadGameEvents`;
  // without this the call thread's `gameSessions.get(...)` returned undefined
  // and every game tile was frozen at the create phase.
  const [convGameRows, setConvGameRows] = useState<
    ReadonlyArray<{ body: string | undefined; isSelf: boolean; senderId: string }>
  >([]);
  useEffect(() => {
    if (conversationId === null) {
      setConvGameRows([]);
      return;
    }
    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;
    void retrieveIdentity().then((identity) => {
      if (cancelled) return;
      const selfId = identity?.accountId ?? '';
      sub = liveQuery(() =>
        db.messages.where('conversationId').equals(conversationId).sortBy('sentAt'),
      ).subscribe({
        next: (rows) => {
          if (cancelled) return;
          setConvGameRows(
            rows.map((r) => ({
              body: r.body,
              isSelf: r.senderAccountId === selfId,
              senderId: r.senderAccountId,
            })),
          );
        },
        error: (err: unknown) => {
          log.warn({ event: 'call.thread.game.subscribe.failed', err: (err as Error).message });
        },
      });
      if (cancelled) sub.unsubscribe();
    });
    return () => {
      cancelled = true;
      sub?.unsubscribe();
    };
  }, [conversationId]);
  const persistedThreadGameEvents = useMemo(
    () => gameEventsFromMessages(convGameRows),
    [convGameRows],
  );
  useEffect(() => {
    replaceFromEvents(persistedThreadGameEvents);
  }, [persistedThreadGameEvents, replaceFromEvents]);

  // Edit-a-self-reply state (mirrors the main chat thread's edit flow).
  const [editingReply, setEditingReply] = useState<{
    id: string;
    body: string;
    contentHash: string;
  } | null>(null);
  const editingMessage = useMemo<Message | null>(() => {
    if (editingReply === null) return null;
    return {
      id: editingReply.id,
      conversationId: conversationId ?? '',
      senderId: localAccountId ?? '',
      body: editingReply.body,
      hlc: '',
      contentHash: editingReply.contentHash,
      timestamp: Date.now(),
      isSelf: true,
      deliveryState: 'sent',
    };
  }, [editingReply, conversationId, localAccountId]);

  // `+` → poll / location / game: stamp the thread root, then open the overlay.
  const openThreadPoll = useCallback((): void => {
    overlayThreadTargetRef.current = threadRootRef.current;
    vm.handlers.openPollComposer();
  }, [vm.handlers, threadRootRef]);
  const openThreadLocation = useCallback((): void => {
    overlayThreadTargetRef.current = threadRootRef.current;
    vm.handlers.openShareLocation();
  }, [vm.handlers, threadRootRef]);
  const openThreadGames = useCallback((): void => {
    overlayThreadTargetRef.current = threadRootRef.current;
    setGamePickerOpen(true);
  }, [threadRootRef]);
  const openThreadMeeting = useCallback((): void => {
    overlayThreadTargetRef.current = threadRootRef.current;
    vm.handlers.openMeetingComposer?.();
  }, [vm.handlers, threadRootRef]);

  const {
    composerProps: baseComposerProps,
    stagedAttachments,
    onRemoveStagedAttachment,
    onFlushStagedAttachments,
  } = useThreadComposer({
    threadIdRef: threadRootRef,
    conversationId,
    ...(conversationName !== undefined ? { conversationName } : {}),
    client,
    mentionCandidates,
    onOpenPoll: openThreadPoll,
    onOpenLocation: openThreadLocation,
    onOpenGames: openThreadGames,
    onOpenMeeting: openThreadMeeting,
  });

  const resolveThreadReaderName = useCallback(
    (userId: string): string | undefined => {
      const accountId = roster.memberDeviceAccounts?.[userId];
      return (
        roster.memberDisplayNames?.[userId] ??
        (accountId !== undefined ? roster.memberDisplayNames?.[accountId] : undefined) ??
        roster.memberDeviceNames?.[userId]
      );
    },
    [roster.memberDeviceAccounts, roster.memberDisplayNames, roster.memberDeviceNames],
  );
  const resolveThreadReaderAvatar = useCallback(
    (userId: string): string | undefined => {
      const accountId = roster.memberDeviceAccounts?.[userId];
      const avatars = roster.memberAvatarUris;
      return avatars?.[userId] ?? (accountId !== undefined ? avatars?.[accountId] : undefined);
    },
    [roster.memberDeviceAccounts, roster.memberAvatarUris],
  );
  // Resolve a poll voter account id → { initials, avatarUrl?, name } for the
  // poll tile avatar stack + the "Poll votes" panel (parity with the main chat).
  const resolveThreadPollVoter = useCallback(
    (id: string): PollVoterAvatar => {
      const me = localAccountId;
      const isSelf = id === me || id === '__local__';
      const name = isSelf ? 'You' : resolveThreadReaderName(id) ?? 'Member';
      const avatarUrl = isSelf
        ? me !== null
          ? resolveThreadReaderAvatar(me)
          : undefined
        : resolveThreadReaderAvatar(id);
      const trimmed = name.trim();
      const initials = trimmed.length > 0 ? trimmed.charAt(0).toUpperCase() : '?';
      return avatarUrl !== undefined && avatarUrl.length > 0
        ? { initials, avatarUrl, name }
        : { initials, name };
    },
    [localAccountId, resolveThreadReaderName, resolveThreadReaderAvatar],
  );
  const resolveThreadVotersByOption = useCallback(
    (
      raw: Record<number, ReadonlyArray<string>>,
    ): Record<number, ReadonlyArray<PollVoterAvatar>> => {
      const out: Record<number, ReadonlyArray<PollVoterAvatar>> = {};
      for (const [idx, ids] of Object.entries(raw)) {
        out[Number(idx)] = ids.map(resolveThreadPollVoter);
      }
      return out;
    },
    [resolveThreadPollVoter],
  );
  // Canonicalise a reply author / mention id → the ACCOUNT id the profile
  // endpoint needs (a peer's `authorId` is a device hash; a 1:1 sender isn't
  // the peer's account id) + a name/avatar hint. Without this the popover fetch
  // 400s → "Profile unavailable". Mirrors the main chat's resolveProfileTarget.
  const resolveThreadProfileTarget = useCallback(
    (raw: string): { accountId: string; hint: { displayName?: string; avatarUri?: string } } | null => {
      if (raw.length === 0) return null;
      if (!roster.isGroup) {
        // 1:1 → the peer is the non-self member.
        const peerId =
          roster.memberAccountIds?.find((id) => id !== localAccountId) ??
          roster.memberDeviceAccounts?.[raw] ??
          raw;
        const avatarUri = roster.memberAvatarUris?.[peerId] ?? roster.memberAvatarUris?.[raw];
        return {
          accountId: peerId,
          hint: {
            ...(conversationName !== undefined && conversationName.length > 0
              ? { displayName: conversationName }
              : {}),
            ...(avatarUri !== undefined ? { avatarUri } : {}),
          },
        };
      }
      const accountId = roster.memberDeviceAccounts?.[raw] ?? raw;
      const displayName =
        roster.memberDisplayNames?.[accountId] ??
        roster.memberDisplayNames?.[raw] ??
        roster.memberDeviceNames?.[raw];
      const avatarUri = roster.memberAvatarUris?.[accountId] ?? roster.memberAvatarUris?.[raw];
      return {
        accountId,
        hint: {
          ...(displayName !== undefined && displayName.length > 0 ? { displayName } : {}),
          ...(avatarUri !== undefined ? { avatarUri } : {}),
        },
      };
    },
    [roster, localAccountId, conversationName],
  );

  // Forward flow — open a picker seeded with the reply, re-send on pick.
  const [forwardSource, setForwardSource] = useState<{
    body: string;
    contentHash: string;
    senderId: string;
  } | null>(null);

  const sendToConv = useCallback(
    async (bytes: Uint8Array): Promise<void> => {
      if (conversationId === null) return;
      const conv = await resolveActiveConv({ client, conversationId, conversationName });
      if (conv === null) return;
      await conv.send(bytes);
    },
    [client, conversationId, conversationName],
  );

  const hashFor = useCallback(async (reply: ThreadReply): Promise<string | null> => {
    const row = await db.messages.get(reply.id as unknown as string);
    return row?.contentHash && row.contentHash.length > 0 ? row.contentHash : null;
  }, []);

  // Persist a LOCAL echo for a self-sent thread reply (self-sends don't round-
  // trip through `onMessage`, so without this the creator never sees their own
  // poll / location / game in the thread). Stamps `inReplyTo` = the thread root
  // so the gateway folds it under the thread. Mirrors ChatRouteHost.
  const persistEcho = useCallback(
    async (
      envelopeBody: unknown,
      sent: { content_hash: Uint8Array; epoch: number | bigint },
      idPrefix: string,
      previewLabel: string,
    ): Promise<void> => {
      if (conversationId === null) return;
      const identity = await retrieveIdentity();
      if (identity === null) return;
      const threadRoot = threadRootRef.current;
      const sentAt = Date.now();
      await sharedPersistSentMessage({
        messages: webMessagesPersister,
        conversations: webConversationsPersister,
        record: {
          id: localEchoId(idPrefix),
          conversationId,
          epoch: typeof sent.epoch === 'bigint' ? Number(sent.epoch) : sent.epoch,
          serverSeq: sentAt,
          contentHash: bytesToHex(sent.content_hash),
          senderAccountId: identity.accountId,
          body: JSON.stringify(envelopeBody),
          sentAt,
          deliveryState: 'sent',
          ...(threadRoot !== null
            ? { inReplyTo: { messageId: threadRoot, preview: previewLabel } }
            : {}),
        },
      });
    },
    [conversationId, threadRootRef],
  );

  // Emit a game move from a thread tile → nests under the thread root
  // (`parent_id`), optimistic fold + local echo. Mirrors the main chat thread's
  // `handleThreadGameMove`.
  const handleThreadGameMove = useCallback(
    (sessionId: string, moveIdx: number, payload: string): void => {
      if (conversationId === null) return;
      const threadTarget = threadRootRef.current;
      void (async () => {
        try {
          const conv = await resolveActiveConv({ client, conversationId, conversationName });
          if (conv === null) return;
          const body = makeMove(sessionId, { moveIdx, payload });
          const envelopeBody = {
            kind: 'com.ping.game_move' as const,
            payload: { ...body },
            ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
          };
          const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
            content_hash: Uint8Array;
            epoch: number | bigint;
          };
          applyGameEvent({ type: 'move', sessionId, moveIdx, payload, by: '__self__' });
          await persistEcho(envelopeBody, sent, 'local-callthreadgamemove', 'Game move');
        } catch (err) {
          log.warn({ event: 'call.thread.game.move.failed', err: (err as Error).message });
        }
      })();
    },
    [client, conversationId, conversationName, makeMove, applyGameEvent, persistEcho, threadRootRef],
  );

  // Begin editing a self reply — text-only, mirrors the main chat thread.
  const beginEditReply = useCallback((reply: ThreadReply): void => {
    const rawBody = reply.rawBody;
    if (rawBody !== undefined && envelopeKind(rawBody) !== undefined && envelopeKind(rawBody) !== 'com.ping.text') {
      pingToast.info({ title: 'Only text replies can be edited' });
      return;
    }
    void (async () => {
      const row = await db.messages.get(reply.id as unknown as string);
      const contentHash = row?.contentHash ?? '';
      setEditingReply({
        id: reply.id as unknown as string,
        body: reply.rawBody ?? reply.body,
        contentHash,
      });
    })();
  }, []);
  const cancelEditReply = useCallback((): void => setEditingReply(null), []);
  const submitEditReply = useCallback(
    async (newText: string): Promise<void> => {
      const target = editingReply;
      const threadRoot = threadRootRef.current;
      if (target === null || conversationId === null) return;
      const trimmed = newText.trim();
      if (trimmed.length === 0) return;
      const conv = await resolveActiveConv({ client, conversationId, conversationName });
      if (conv === null) throw new Error('Not connected — try again in a moment.');
      if (target.contentHash.length > 0) {
        await conv.send(
          encodeEnvelopeJsonBytes({
            kind: 'com.ping.edit',
            payload: { target: target.contentHash, body: trimmed },
          }),
        );
      }
      // Optimistic local rewrite preserving the com.ping.text + parent_id shape.
      const newEnvelope = JSON.stringify({
        kind: 'com.ping.text',
        payload: { body: trimmed, thread: true },
        ...(threadRoot !== null ? { parent_id: threadRoot } : {}),
      });
      await db.messages
        .update(target.id, { body: newEnvelope, editedAt: Date.now() })
        .catch(() => undefined);
      setEditingReply(null);
    },
    [editingReply, conversationId, client, conversationName, threadRootRef],
  );

  const toggleReaction = useCallback(
    (reply: ThreadReply, emoji: string): void => {
      const me = localAccountIdRef.current ?? '__local__';
      void (async () => {
        const target = await hashFor(reply);
        if (target === null) return;
        const replyId = reply.id as unknown as string;
        const current = reactionsByMessageId[replyId] ?? [];
        const reactedNow = current.find((r) => r.emoji === emoji)?.reactedByMe === true;
        const nextReacted = !reactedNow;
        try {
          if (nextReacted) {
            await webReactionsPersister.put({
              id: `${target}:${emoji}:${me}`,
              conversationId: conversationId ?? '',
              targetContentHash: target,
              targetMessageId: replyId,
              emoji,
              actorAccountId: me,
              addedAt: Date.now(),
            });
          } else {
            await webReactionsPersister.deleteByKey(conversationId ?? '', target, emoji, me);
          }
        } catch (err) {
          log.warn({ event: 'call.thread.reaction.persist.failed', err: (err as Error).message });
        }
        try {
          await sendToConv(
            encodeEnvelopeJsonBytes({
              kind: nextReacted ? 'com.ping.reaction' : 'com.ping.reaction_remove',
              payload: { target, emoji, actorAccountId: me },
            }),
          );
        } catch (err) {
          log.warn({ event: 'call.thread.reaction.send.failed', err: (err as Error).message });
        }
      })();
    },
    [conversationId, hashFor, reactionsByMessageId, sendToConv],
  );

  const retryReply = useCallback(
    (reply: ThreadReply): void => {
      if (conversationId === null) return;
      void (async () => {
        try {
          const conv = await resolveActiveConv({ client, conversationId, conversationName });
          if (conv === null) return;
          const rawBody = reply.rawBody;
          const bytes =
            rawBody !== undefined && rawBody.charAt(0) === '{'
              ? new TextEncoder().encode(rawBody)
              : encodeEnvelopeJsonBytes({
                  kind: 'com.ping.text',
                  payload: { body: reply.body, thread: true },
                  parent_id: reply.threadId as unknown as string,
                });
          await conv.send(bytes);
          // Flip the failed local echo to sent (self-sends don't echo back).
          await db.messages
            .update(reply.id as unknown as string, { deliveryState: 'sent' })
            .catch(() => undefined);
        } catch (err) {
          log.warn({ event: 'call.thread.retry.failed', err: (err as Error).message });
        }
      })();
    },
    [client, conversationId, conversationName],
  );

  // Open a thread doc reply — fetch + decrypt the blob, open in a new tab.
  const handleDocTap = useCallback((rawBody: string): void => {
    const payload = parseFileEnvelope(rawBody);
    if (payload === undefined || payload.blob_ref.media_id === 'pending') return;
    void (async () => {
      try {
        const blob = await fetchAndDecryptMediaBlob(payload.blob_ref);
        const typed = payload.mime.length > 0 ? new Blob([blob], { type: payload.mime }) : blob;
        const url = URL.createObjectURL(typed);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (err) {
        log.warn({ event: 'call.thread.doc.open.failed', err: (err as Error).message });
      }
    })();
  }, []);

  const handleForwardPick = useCallback(
    (targetConversationId: string): void => {
      const source = forwardSource;
      setForwardSource(null);
      if (source === null) return;
      void (async () => {
        try {
          const conv = await resolveActiveConv({
            client,
            conversationId: targetConversationId,
            conversationName: forwardTargets.find((t) => t.id === targetConversationId)?.name,
          });
          if (conv === null) return;
          const parsed = parseEnvelope(source.body);
          const innerPayload =
            parsed && parsed.kind === 'com.ping.text'
              ? (parsed.body as Record<string, unknown>)
              : { body: lastMessagePreview(source.body) ?? '' };
          if (
            typeof (innerPayload as { body?: unknown }).body === 'string' &&
            ((innerPayload as { body?: string }).body ?? '').length === 0
          ) {
            return;
          }
          await conv.send(
            encodeEnvelopeJsonBytes({
              kind: 'com.ping.forward',
              payload: {
                source_event_id: source.contentHash,
                source_conversation_id: conversationId ?? '',
                content_type: 0x11,
                content_body: innerPayload,
                ...(source.senderId.length > 0 ? { original_sender: source.senderId } : {}),
              },
            }),
          );
          pingToast.success({ title: 'Message forwarded' });
        } catch (err) {
          log.warn({ event: 'call.thread.forward.failed', err: (err as Error).message });
        }
      })();
    },
    [client, conversationId, forwardSource, forwardTargets],
  );

  // Cast a poll vote from a thread poll tile — optimistic local OR-set replace
  // + a `com.ping.poll_vote` envelope (parity with the main chat thread).
  const voteThreadPoll = useCallback(
    (pollTarget: string, optionIndex: number): void => {
      const me = localAccountIdRef.current ?? '__local__';
      const now = Date.now();
      const replyId = Object.keys(pollTargetByReplyIdRef.current).find(
        (id) => pollTargetByReplyIdRef.current[id] === pollTarget,
      );
      const rawBody =
        replyId !== undefined
          ? threadRepliesRef.current.find((r) => r.id === replyId)?.rawBody
          : undefined;
      let multiSelect = false;
      if (rawBody !== undefined) {
        const parsed = parseEnvelope(rawBody);
        if (parsed?.kind === 'com.ping.poll') {
          multiSelect = (parsed.body as { multi_select?: boolean }).multi_select === true;
        }
      }
      let nextSet: number[];
      if (multiSelect) {
        const current = pollVotesByTargetRef.current[pollTarget]?.myVotes ?? [];
        nextSet = current.includes(optionIndex)
          ? current.filter((i) => i !== optionIndex)
          : [...current, optionIndex];
      } else {
        nextSet = [optionIndex];
      }
      if (webPollVotesPersister.replaceActorVotes !== undefined) {
        void webPollVotesPersister.replaceActorVotes(pollTarget, me, nextSet, now);
      }
      void sendToConv(
        encodeEnvelopeJsonBytes({
          kind: 'com.ping.poll_vote',
          payload: multiSelect
            ? { target: pollTarget, choices: nextSet, actorAccountId: me }
            : { target: pollTarget, choices: nextSet, option_index: optionIndex, actorAccountId: me },
        }),
      ).catch((err) => log.warn({ event: 'call.thread.poll.vote.failed', err: (err as Error).message }));
    },
    [sendToConv],
  );

  const rowActions = useMemo<ThreadRowActions>(() => {
    const threadGroupNonSelfCount = roster.isGroup ? roster.memberAccountIds?.length ?? 0 : 0;
    return {
      onReact: (reply) => toggleReaction(reply, '👍'),
      onToggleReaction: (reply, emoji) => toggleReaction(reply, emoji),
      reactionsByReplyId: reactionsByMessageId,
      // Reflect pinned/saved/starred state in the reply action menu so the
      // entries read Unpin/Unsave/Unstar (resolve the reply's hash → set).
      isReplyPinned: (reply: ThreadReply): boolean => {
        const h = hashByReplyId[reply.id];
        return h !== undefined && pinnedHashes.has(h);
      },
      isReplySaved: (reply: ThreadReply): boolean => {
        const h = hashByReplyId[reply.id];
        return h !== undefined && savedHashes.has(h);
      },
      isReplyStarred: (reply: ThreadReply): boolean => {
        const h = hashByReplyId[reply.id];
        return h !== undefined && starredHashes.has(h);
      },
      onMenuAction: (action: MessageMenuAction, reply) => {
        void (async () => {
          // Copy the DECODED text (parity with the main chat) — `reply.body` is
          // only the truncated preview; decode `rawBody` for the full message,
          // and toast so the action gives the same feedback as the main chat.
          if (action === 'copy') {
            let text = reply.body;
            const raw = reply.rawBody;
            if (raw !== undefined && raw.length > 0) {
              const parsed = parseEnvelope(raw);
              if (parsed?.kind === 'com.ping.text') {
                text = (parsed.body as { body: string }).body;
              }
            }
            try {
              if (typeof navigator !== 'undefined' && navigator.clipboard) {
                await navigator.clipboard.writeText(text);
              }
              pingToast.success({ title: 'Message copied' });
            } catch {
              /* clipboard denied */
            }
            return;
          }
          const target = await hashFor(reply);
          if (action === 'remove') {
            // MY OWN reply → redact for everyone + local delete; SOMEONE ELSE'S
            // reply → delete-for-me only (never broadcast a redact for a peer's
            // message). The local delete drops the Dexie row so the thread's
            // liveQuery refreshes it out of view.
            const isOwn = reply.isSelf === true;
            const removeLocal = async (): Promise<void> => {
              try {
                if (target !== null && conversationId !== null) {
                  await addLocallyRemovedMessage(conversationId, target);
                }
                const row =
                  target !== null
                    ? await webMessagesPersister.getByContentHash(target)
                    : undefined;
                await webMessagesPersister.delete(row?.id ?? (reply.id as unknown as string));
              } catch {
                /* best effort */
              }
            };
            try {
              if (isOwn && target !== null) {
                await sendToConv(
                  encodeEnvelopeJsonBytes({ kind: 'com.ping.redact', payload: { target, reason: 'user' } }),
                );
              }
              await removeLocal();
            } catch {
              pingToast.error({ title: "Couldn't remove", description: 'Try again in a moment.' });
            }
            return;
          }
          if (target === null) return;
          if (action === 'pin') {
            // Toggle against the committed pin set (shared localStorage key with
            // the main room) so a second tap unpins instead of re-pinning.
            const willPin =
              conversationId === null
                ? true
                : !(await webPinnedPersister.listByConversation(conversationId)).includes(target);
            if (conversationId !== null) await webPinnedPersister.set(conversationId, target, willPin);
            const identity = await retrieveIdentity();
            await sendToConv(
              encodeEnvelopeJsonBytes({
                kind: willPin ? 'com.ping.pin' : 'com.ping.unpin',
                payload: {
                  target,
                  ...(identity !== null ? { actorDisplayName: identity.displayName } : {}),
                },
              }),
            );
            pingToast.success({ title: willPin ? 'Message pinned' : 'Message unpinned' });
          } else if (action === 'save') {
            // Toggle against the committed saved set (mirrors the main room).
            const willSave =
              conversationId === null
                ? true
                : !(await webSavedPersister.listByConversation(conversationId)).includes(target);
            if (conversationId !== null) await webSavedPersister.set(conversationId, target, willSave);
            await sendToConv(
              encodeEnvelopeJsonBytes({
                kind: willSave ? 'com.ping.save' : 'com.ping.unsave',
                payload: { target },
              }),
            );
            pingToast.success({ title: willSave ? 'Message saved' : 'Removed from saved' });
          } else if (action === 'star') {
            // Star/unstar (§2.7 0x64) — DeviceGroup-synced flat set. Optimistic
            // local write + best-effort broadcast to the user's other devices.
            const willStar = !(await webStarredPersister.list()).includes(target);
            await webStarredPersister.set(target, willStar);
            void sendStar(client, target, willStar).catch(() => undefined);
            pingToast.success({ title: willStar ? 'Message starred' : 'Removed from starred' });
          } else if (action === 'forward') {
            setForwardSource({
              body: reply.rawBody ?? reply.body,
              contentHash: target,
              senderId: reply.authorId as unknown as string,
            });
          }
        })();
      },
      // Fully-interactive variant — parity with the main chat thread: image
      // lightbox, inline video playback, live poll tally + voting, voice
      // playback, doc/location/link openers. Media that isn't yet decrypted
      // falls back to the context-free display variant until it resolves.
      buildVariant: (reply: ThreadReply): ChatMessageVariant | undefined => {
        const rawBody = reply.rawBody;
        if (rawBody === undefined || rawBody.length === 0) return undefined;
        const kind = envelopeKind(rawBody);
        const replyId = reply.id as unknown as string;

        if (kind === 'com.ping.image') {
          const imageUri = imagePreviewByReplyId[replyId];
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            image: {
              onTapOpen: () => {
                if (imageUri !== undefined) setLightboxUri(imageUri);
              },
              ...(imageUri !== undefined ? { imageUri } : {}),
            },
          });
        }

        if (kind === 'com.ping.poll') {
          const target = pollTargetByReplyId[replyId];
          if (target === undefined) return undefined;
          const tally = pollVotesByTarget[target] ?? { counts: {}, total: 0, votersByOption: {} };
          // Resolve each voter → real name + avatar for BOTH the tile's
          // per-option avatar stack AND the "Poll votes" panel.
          const resolvedVotersByOption = resolveThreadVotersByOption(tally.votersByOption);
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            poll: {
              counts: tally.counts,
              ...(tally.myVote !== undefined ? { myVote: tally.myVote } : {}),
              ...(tally.myVotes !== undefined ? { myVotes: tally.myVotes } : {}),
              total: tally.total,
              votersByOption: resolvedVotersByOption,
              onVote: (optionIndex: number) => voteThreadPoll(target, optionIndex),
              onViewVotes: () => {
                const parsed = parseEnvelope(rawBody);
                if (parsed?.kind !== 'com.ping.poll') return;
                const b = parsed.body as {
                  question: string;
                  options: readonly string[];
                  multi_select?: boolean;
                  anonymous?: boolean;
                  closes_at?: number;
                };
                setVotesPanel({
                  poll: {
                    pollTarget: target,
                    question: b.question,
                    options: b.options,
                    multipleChoice: b.multi_select === true,
                    ...(b.anonymous === true ? { anonymous: true } : {}),
                    ...(b.closes_at !== undefined ? { closesAt: b.closes_at } : {}),
                    creatorName: '',
                    createdAt: Date.now(),
                  },
                  counts: tally.counts,
                  total: tally.total,
                  votersByOption: resolvedVotersByOption,
                });
              },
            },
          });
        }

        if (kind === 'com.ping.voice') {
          const payload = parseVoiceEnvelope(rawBody);
          if (payload === undefined) return undefined;
          return {
            kind: 'voice',
            state: reply.isSelf ? 'sent' : 'received',
            durationMs: payload.durationMs,
            waveform: CALL_THREAD_WAVEFORM,
            progress: 0,
            onTogglePlay: () => undefined,
            resolveVoiceSource: resolveCallVoiceSource,
            ...(payload.transcript !== undefined && payload.transcript.length > 0
              ? { transcript: payload.transcript }
              : {}),
          };
        }

        if (kind === 'com.ping.video') {
          const media = videoByReplyId[replyId];
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            video: {
              onTapOpen: () => {
                if (media?.videoUri !== undefined) {
                  window.open(media.videoUri, '_blank', 'noopener,noreferrer');
                }
              },
              ...(media?.videoUri !== undefined ? { videoUri: media.videoUri } : {}),
              ...(media?.posterUri !== undefined ? { posterUri: media.posterUri } : {}),
            },
          });
        }

        if (kind === 'com.ping.file') {
          return deriveVariantFromBody(rawBody, {
            message: threadReplyMessage(reply),
            doc: { onTapPreview: () => handleDocTap(rawBody) },
          });
        }

        const derived = deriveVariantFromBody(rawBody, { message: threadReplyMessage(reply) });
        if (derived?.kind === 'location') {
          const { lat, lng } = derived;
          return {
            ...derived,
            onOpen: () =>
              window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank', 'noopener,noreferrer'),
          };
        }
        if (derived?.kind === 'linkPreview') {
          const href = derived.url;
          return { ...derived, onOpen: () => window.open(href, '_blank', 'noopener,noreferrer') };
        }
        return derived;
      },
      // Live, folded game session per game reply → interactive tile (current
      // phase + moves) instead of a frozen create card. Parity with the main
      // chat thread.
      gameSessionForReply: (reply: ThreadReply) => {
        const rawBody = reply.rawBody;
        if (rawBody === undefined || rawBody.length === 0) return undefined;
        const parsed = parseEnvelope(rawBody);
        if (
          parsed?.kind !== 'com.ping.game_create' &&
          parsed?.kind !== 'com.ping.game_move' &&
          parsed?.kind !== 'com.ping.game_outcome'
        ) {
          return undefined;
        }
        const sessionId = (parsed.body as { session_id?: string }).session_id;
        if (sessionId === undefined || sessionId.length === 0) return undefined;
        return gameSessions.get(sessionId);
      },
      onGameMove: handleThreadGameMove,
      ...(gameOpponentName !== undefined ? { gameOpponentName } : {}),
      resolveReaderName: resolveThreadReaderName,
      resolveReaderAvatar: resolveThreadReaderAvatar,
      ...(roster.isGroup && threadGroupNonSelfCount > 0
        ? { groupNonSelfMemberCount: threadGroupNonSelfCount }
        : {}),
      onEditReply: beginEditReply,
      onRetryReply: retryReply,
      // Avatar tap → anchored profile popover; @mention tap → full profile
      // modal (both rendered by the call-local ProfileOverlaysHost in
      // `overlayHost`). Canonicalise the id to an ACCOUNT id first (a peer's
      // `authorId` is a device hash) or the fetch 400s → "Profile unavailable".
      onAvatarPress: (userId: string, anchorRef: React.RefObject<unknown>) => {
        const t = resolveThreadProfileTarget(userId);
        if (t === null) return;
        vm.handlers.openMiniProfile(t.accountId as never, anchorRef);
      },
      onMentionPress: (userId: string) => {
        const t = resolveThreadProfileTarget(userId);
        if (t === null) return;
        vm.handlers.openUserProfile(t.accountId as never, t.hint);
      },
    };
  }, [
    conversationId,
    roster.isGroup,
    roster.memberAccountIds,
    reactionsByMessageId,
    toggleReaction,
    hashFor,
    sendToConv,
    resolveThreadReaderName,
    resolveThreadReaderAvatar,
    retryReply,
    imagePreviewByReplyId,
    videoByReplyId,
    pollTargetByReplyId,
    pollVotesByTarget,
    voteThreadPoll,
    handleDocTap,
    gameSessions,
    handleThreadGameMove,
    gameOpponentName,
    beginEditReply,
    vm.handlers,
    resolveThreadVotersByOption,
    resolveThreadProfileTarget,
    hashByReplyId,
    pinnedHashes,
    savedHashes,
    starredHashes,
    client,
  ]);

  // Merge edit-mode props into the composer so a self reply's hover-pencil
  // (rowActions.onEditReply) puts the composer into edit mode + routes Send →
  // onSubmitEdit. Parity with the main chat thread.
  const composerProps = useMemo<ThreadComposerProps>(
    () => ({
      ...baseComposerProps,
      editingMessage,
      onSubmitEdit: submitEditReply,
      onCancelEdit: cancelEditReply,
    }),
    [baseComposerProps, editingMessage, submitEditReply, cancelEditReply],
  );

  const forwardOverlay = (
    <>
      <ForwardOverlay
        open={forwardSource !== null}
        conversations={forwardTargets}
        onPick={handleForwardPick}
        onClose={() => setForwardSource(null)}
      />
      <ImageLightbox
        open={lightboxUri !== null}
        {...(lightboxUri !== null ? { imageUri: lightboxUri } : {})}
        onClose={() => setLightboxUri(null)}
      />
      {votesPanel !== null ? (
        <PollVotesPanel
          visible
          poll={votesPanel.poll}
          counts={votesPanel.counts}
          total={votesPanel.total}
          {...(votesPanel.votersByOption !== undefined
            ? { votersByOption: votesPanel.votersByOption }
            : {})}
          onClose={() => setVotesPanel(null)}
        />
      ) : null}
    </>
  );

  // Call-local overlay host: profile popover/modal + poll / location / game
  // creation, all driven by the call-local `vm` (poll/location) + a boolean
  // (game). Each creation stamps `parent_id` + `thread:true` (via
  // `overlayThreadTargetRef`) so it nests under the call thread and never
  // leaks into the main timeline.
  const overlayHost = (
    <>
      <ProfileOverlaysHost vm={vm} />
      <PollComposerHost
        vm={vm}
        onSubmitDraft={async (payload) => {
          if (conversationId === null) return;
          const pollState = payload.state;
          const closeMsByPreset: Record<string, number | undefined> = {
            '1h': 60 * 60 * 1000,
            '24h': 24 * 60 * 60 * 1000,
            '7d': 7 * 24 * 60 * 60 * 1000,
            never: undefined,
          };
          const closeDelta = closeMsByPreset[pollState.duration];
          const threadTarget = overlayThreadTargetRef.current;
          overlayThreadTargetRef.current = null;
          try {
            const conv = await resolveActiveConv({ client, conversationId, conversationName });
            if (conv === null) return;
            const envelopeBody = {
              kind: 'com.ping.poll' as const,
              payload: {
                question: pollState.question,
                options: pollState.options.map((o) => o.text.trim()).filter((t) => t.length > 0),
                multi_select: pollState.kind === 'multiple',
                anonymous: pollState.anonymous,
                ...(closeDelta !== undefined ? { closes_at: Date.now() + closeDelta } : {}),
                ...(threadTarget !== null ? { thread: true } : {}),
              },
              ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
            };
            const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
              content_hash: Uint8Array;
              epoch: number | bigint;
            };
            await persistEcho(envelopeBody, sent, 'local-callpoll', 'Poll');
          } catch (err) {
            log.warn({ event: 'call.thread.poll.create.failed', err: (err as Error).message });
            pingToast.error({ title: "Couldn't create poll", description: 'Try again in a moment.' });
          }
        }}
      />
      <ShareLocationHost
        vm={vm}
        {...(conversationName !== undefined ? { conversationName } : {})}
        onShare={async (result) => {
          if (conversationId === null) return;
          const { location, liveDurationSeconds } = result;
          const threadTarget = overlayThreadTargetRef.current;
          overlayThreadTargetRef.current = null;
          try {
            const conv = await resolveActiveConv({ client, conversationId, conversationName });
            if (conv === null) return;
            const envelopeBody = {
              kind: 'com.ping.location' as const,
              payload: {
                lat: location.lat,
                lon: location.lng,
                accuracy_m: 0,
                ...(location.label !== undefined ? { label: location.label } : {}),
                ...(result.kind === 'live' && liveDurationSeconds !== undefined
                  ? { live_duration_s: liveDurationSeconds }
                  : {}),
                ...(threadTarget !== null ? { thread: true } : {}),
              },
              ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
            };
            const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
              content_hash: Uint8Array;
              epoch: number | bigint;
            };
            await persistEcho(envelopeBody, sent, 'local-calllocation', '📍 Location');
          } catch (err) {
            log.warn({ event: 'call.thread.location.share.failed', err: (err as Error).message });
            pingToast.error({
              title: "Couldn't share location",
              description: 'Try again in a moment.',
            });
          }
        }}
        onSearch={(query, signal) => searchPlaces(query, signal)}
        onResolveCurrentLocation={(signal) => resolveCurrentLocation(signal)}
      />
      <GamePickerOverlay
        open={gamePickerOpen}
        onClose={() => setGamePickerOpen(false)}
        onPick={(kind) => {
          setGamePickerOpen(false);
          if (conversationId === null) return;
          const threadTarget = overlayThreadTargetRef.current;
          overlayThreadTargetRef.current = null;
          void (async () => {
            try {
              const { sessionId, body } = createGame(kind);
              const conv = await resolveActiveConv({ client, conversationId, conversationName });
              if (conv === null) return;
              const envelopeBody = {
                kind: 'com.ping.game_create' as const,
                payload: { ...body, ...(threadTarget !== null ? { thread: true } : {}) },
                ...(threadTarget !== null ? { parent_id: threadTarget } : {}),
              };
              const sent = (await conv.send(encodeEnvelopeJsonBytes(envelopeBody))) as {
                content_hash: Uint8Array;
                epoch: number | bigint;
              };
              await persistEcho(envelopeBody, sent, 'local-callgame', 'Game');
              applyGameEvent({ type: 'create', sessionId, gameKind: kind, createdBy: '__self__' });
            } catch (err) {
              log.warn({ event: 'call.thread.game.create.failed', err: (err as Error).message });
              pingToast.error({ title: "Couldn't start game", description: 'Try again in a moment.' });
            }
          })();
        }}
      />
    </>
  );

  return {
    rowActions,
    composerProps,
    stagedAttachments,
    onRemoveStagedAttachment,
    onFlushStagedAttachments,
    forwardOverlay,
    overlayHost,
  };
}
